import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, RevealWords, RevealSide, Overline } from "./shared";

const ABSTRACT =
  "https://images.unsplash.com/photo-1556691421-cf15fe27a0b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      ref={ref}
      id="about"
      data-testid="about-section"
      className="relative mx-auto max-w-7xl px-6 py-16 md:py-24"
    >
      <div className="grid items-center gap-14 md:grid-cols-2">
        <div>
          <Reveal>
            <Overline>Who we are</Overline>
          </Reveal>
          <RevealWords
            text="A single partner for every layer of your technology"
            className="font-display mt-5 text-4xl font-semibold leading-tight text-white md:text-5xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-[#a0aec0] md:text-lg">
              Gabicom blends deep engineering expertise with sharp creative craft.
              We help businesses procure the right hardware, migrate to the cloud,
              secure their systems and build brands that stand out — all under one roof.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-6">
            {[
              ["End-to-end", "From procurement to deployment and support"],
              ["Human support", "Real engineers, available around the clock"],
              ["Security-first", "Every solution hardened by design"],
              ["Creative edge", "Design that makes technology feel effortless"],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={0.15 + i * 0.08}>
                <div className="border-l border-white/10 pl-4">
                  <p className="font-display text-lg text-white">{t}</p>
                  <p className="mt-1 text-sm text-[#a0aec0]">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="glow-blob left-1/4 top-1/4 h-64 w-64 bg-[#b026ff]" />
          <RevealSide from="right" className="relative overflow-hidden rounded-3xl border border-white/10">
            <motion.img
              style={{ y: imgY }}
              src={ABSTRACT}
              alt="Abstract technology visualization"
              className="h-[520px] w-full scale-110 object-cover"
              data-testid="about-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05050a] via-transparent to-transparent" />
            <div className="glass absolute bottom-5 left-5 right-5 rounded-2xl p-5">
              <p className="font-mono-tech text-xs uppercase tracking-widest text-[#00f0ff]">
                Est. Trusted Technology Partner
              </p>
              <p className="mt-2 font-display text-2xl text-white">
                Consulting · Cloud · Security · Design
              </p>
            </div>
          </RevealSide>
        </div>
      </div>
    </section>
  );
}
